// Phase 84.M.86 — In-app browser detection helpers.
//
// User report: VĐV mở PickLeague từ Zalo / Messenger / Facebook / Instagram /
// TikTok thì login Google fail, mất session, lỗi upload ảnh, lỗi thanh toán,
// lỗi livestream. Lý do: các in-app browser dùng webview hạn chế cookies +
// chặn nhiều API (OAuth popup, clipboard, fullscreen…). Solution: detect ngay
// khi user landing → cảnh báo + hướng dẫn mở bằng Safari/Chrome.
//
// Mục tiêu KHÔNG redirect cứng (vì iOS/Android không cho), chỉ:
//   • Modal popup 1 lần/session ở app start.
//   • Banner sticky top cho phép user dismiss (cùng session).
//   • Inline warning ở các bước nhạy cảm (Login Google, Payment, Upload,
//     Livestream).
//
// User choices Phase 84.M.86:
//   • 1bc — Modal pop 1 lần + banner sticky top sau đó.
//   • 2ac — 1 lần/session + re-show ở critical routes.
//   • 3a  — Re-show inline banner ở Login Google / Payment / Upload / Livestream.
//   • 4a  — Premium dark style + Safari/Chrome SVG icon.
//   • 5a  — Chỉ mobile (iOS/Android), bỏ qua desktop.
//   • 6b  — Không cần debug query param.
//   • 7c  — Copy URL kèm UTM `?from=inapp`.

const UA_PATTERNS = [
  // Facebook in-app browser (Facebook for iOS/Android).
  { re: /FBAN|FBAV|FB_IAB|FBIOS|FBDV/i, name: "Facebook", id: "facebook" },
  // Messenger in-app browser (cũng dùng FB UA nhưng kèm Messenger).
  { re: /FB_IAB\/MESSENGER|FBAN\/MessengerForiOS|FBAN\/FB4A.*Messenger|MessengerLiteForiOS|Messenger\//i, name: "Messenger", id: "messenger" },
  // Instagram in-app browser.
  { re: /Instagram/i, name: "Instagram", id: "instagram" },
  // Zalo browser (iOS / Android).
  { re: /Zalo|ZaloTheme|ZaloApp/i, name: "Zalo", id: "zalo" },
  // TikTok in-app webview (musical_ly, Bytedance).
  { re: /musical_ly|Bytedance|TikTok|BytedanceWebview/i, name: "TikTok", id: "tiktok" },
  // LINE in-app browser.
  { re: /\bLine\//i, name: "LINE", id: "line" },
  // Twitter / X in-app browser.
  { re: /Twitter|TwitterAndroid/i, name: "Twitter (X)", id: "twitter" },
  // KakaoTalk.
  { re: /KAKAOTALK/i, name: "KakaoTalk", id: "kakao" },
];

// Detect generic WebView (Android wv flag + iOS WKWebView heuristic).
// LƯU Ý: heuristic iOS không hoàn hảo — Safari standalone (PWA) cũng giống
// WKWebView. Vì vậy ta CHỈ flag generic webview khi KHÔNG match UA cụ thể ở trên.
function isGenericWebView(ua) {
  const lower = ua.toLowerCase();
  // Android WebView: chứa "; wv)" hoặc "Version/x Chrome/x" thiếu "Safari".
  if (/Android/i.test(ua) && /; wv\)/i.test(ua)) return true;
  // iOS in-app webview: KHÔNG có "Safari/" trong UA → khả năng cao là WKWebView
  // (Safari thật luôn có "Safari/").
  if (/(iPhone|iPad|iPod)/i.test(ua) && !lower.includes("safari/")) return true;
  return false;
}

/**
 * @typedef {Object} InAppBrowserInfo
 * @property {boolean} isInApp     — true nếu phát hiện in-app browser.
 * @property {boolean} isMobile    — true nếu user agent là mobile.
 * @property {string|null} app     — tên hiển thị app (vd "Zalo", "Messenger").
 * @property {string|null} appId   — id chuẩn hoá (zalo, messenger, ...).
 * @property {"ios"|"android"|"other"} os
 */

/** Detect in-app browser từ navigator.userAgent. SSR-safe. */
export function detectInAppBrowser(uaOverride) {
  const ua = uaOverride || (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
  if (!ua) {
    return { isInApp: false, isMobile: false, app: null, appId: null, os: "other" };
  }
  // Phase 84.M.105 — PickLeague Mobile (Expo WebView) là legit native shell, KHÔNG phải in-app webview lạ.
  // Detect qua UA suffix (set bởi mobile App.js qua `applicationNameForUserAgent`) hoặc flag JS.
  if (/PickLeague\/[\d.]+\s+Mobile/i.test(ua)) {
    const isIOS = /(iPhone|iPad|iPod)/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    return {
      isInApp: false,
      isMobile: isIOS || isAndroid,
      app: null,
      appId: null,
      os: isIOS ? "ios" : isAndroid ? "android" : "other",
    };
  }
  if (typeof window !== "undefined" && window.__pickleagueNativeApp === true) {
    const isIOS = /(iPhone|iPad|iPod)/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    return {
      isInApp: false,
      isMobile: isIOS || isAndroid,
      app: null,
      appId: null,
      os: isIOS ? "ios" : isAndroid ? "android" : "other",
    };
  }
  const isIOS = /(iPhone|iPad|iPod)/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;
  const os = isIOS ? "ios" : isAndroid ? "android" : "other";

  // Match named in-app browser (cụ thể trước, generic webview sau).
  for (const p of UA_PATTERNS) {
    if (p.re.test(ua)) {
      return { isInApp: true, isMobile, app: p.name, appId: p.id, os };
    }
  }
  // Generic webview fallback — chỉ trigger trên mobile.
  if (isMobile && isGenericWebView(ua)) {
    return { isInApp: true, isMobile, app: "In-app browser", appId: "webview", os };
  }
  return { isInApp: false, isMobile, app: null, appId: null, os };
}

/** Build URL có gắn UTM `?from=inapp` để track copy-link → external browser. */
export function buildShareableUrl(originalHref) {
  try {
    const href = originalHref || (typeof window !== "undefined" ? window.location.href : "");
    if (!href) return "";
    const u = new URL(href);
    if (!u.searchParams.has("from")) {
      u.searchParams.set("from", "inapp");
    }
    return u.toString();
  } catch {
    return originalHref || "";
  }
}

/** sessionStorage helper — dismiss state chỉ tồn tại tới khi đóng tab. */
const SS_MODAL_DISMISSED = "pl_inapp_modal_dismissed";
const SS_BANNER_DISMISSED = "pl_inapp_banner_dismissed";

export function getModalDismissed() {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(SS_MODAL_DISMISSED) === "1";
  } catch {
    return false;
  }
}
export function setModalDismissed() {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SS_MODAL_DISMISSED, "1");
  } catch { /* ignore */ }
}
export function getBannerDismissed() {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(SS_BANNER_DISMISSED) === "1";
  } catch {
    return false;
  }
}
export function setBannerDismissed() {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SS_BANNER_DISMISSED, "1");
  } catch { /* ignore */ }
}
